import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { editIndividualBusinessContentHeader, formOptionsAggregateBusiness, formOptionsBusiness, inputTypeKeyMap } from '../constants';
import { i18n } from '@/i18n';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { NarrowState } from '@/corePagesModule/BaseTableContent/interface/uiState';
import { SortType } from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { diContainer } from '@/coreContainer/container';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { simpleCopy } from '@/utils/deepCopy';

const t = i18n.t;

export const useEditIndividualBusinessStore = defineStore('editIndividualBusiness', () => {
  const state = reactive<IEditIndividualBusinessState>({
    renderingKey: 0,
    isBaseBusinessView: false,
    tableInfo: {
      tableContentProperties: editIndividualBusinessContentHeader,
      tableContentItems: [],
      selectOptionInfo: {},
      isReset: false,
      loadingState: {
        status: { type: 'none' },
      },
      pagingState: initPagingOptions(30),
      sortState: {
        sortType: 'ASC',
        sortTarget: 'id',
      },
      filterState: [],
      childBusiness: {
        tableContentItems: [],
        pagingState: initPagingOptions(50),
        loadingState: {
          status: { type: 'none' },
        },
        returnCount: 0,
      },
    },
    modalState: {
      isOpening: {
        edit: false,
        childBusinessList: false,
        loading: false,
        success: false,
        commonError: false,
        duplicationError: false,
      },
      editState: {
        type: 'none',
        item: BusinessFactory.create({}),
        isEdited: false,
      },
    },
  });

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter('editIndividualBusiness');

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.renderingKey = 0;
    state.tableInfo = {
      tableContentProperties: editIndividualBusinessContentHeader,
      tableContentItems: [],
      selectOptionInfo: {},
      isReset: false,
      loadingState: {
        status: { type: 'none' },
      },
      pagingState: initPagingOptions(30),
      filterState: [],
      sortState: {
        sortType: 'ASC',
        sortTarget: 'id',
      },
      childBusiness: {
        tableContentItems: [],
        pagingState: initPagingOptions(50),
        loadingState: {
          status: { type: 'none' },
        },
        returnCount: 0,
      },
    };
    state.modalState = {
      isOpening: {
        edit: false,
        childBusinessList: false,
        loading: false,
        success: false,
        commonError: false,
        duplicationError: false,
      },
      editState: {
        type: 'none',
        item: BusinessFactory.create({}),
        isEdited: false,
      },
    };
  };

  /**
   * 初期化処理
   */
  const initialization = async () => {
    $reset();
    state.renderingKey += 1;
    await _fetchTable();
  };

  /**
   * 編集モーダルのstateをリセットする
   */
  const resetEditModalState = () => {
    state.modalState.editState = {
      type: 'none',
      item: BusinessFactory.create({}),
      isEdited: false,
    };
  };

  /**
   * APIに集計編集を投げる関数
   */
  const putEditedAggregateBusiness = async () => {
    const checkDuplicationResponse = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(state.modalState.editState.item);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.aggregateBusinessId &&
          checkDuplicationResponse.status.data.aggregateBusinessId !== state.modalState.editState.item.businessId
        ) {
          // 重複がある
          state.modalState.isOpening.loading = false;
          state.modalState.isOpening.duplicationError = true;
          return;
        } else {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）
          const putResponse = await diContainer.putAggregateBusinessUseCase.execute(state.modalState.editState.item);
          if (putResponse.status.type === 'success') {
            state.modalState.isOpening.loading = false;
            state.modalState.isOpening.success = true;
            return;
          }
        }
      case 'error':
      default:
        state.modalState.isOpening.loading = false;
        state.modalState.isOpening.commonError = true;
    }
    return;
  };

  /**
   * APIに関連取引編集を投げる関数
   */
  const putEditedBaseBusiness = async () => {
    const checkDuplicationResponse = await diContainer.checkDuplicateBaseBusinessUseCase.execute(state.modalState.editState.item);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.baseBusinessId &&
          checkDuplicationResponse.status.data.baseBusinessId !== state.modalState.editState.item.businessId
        ) {
          // 重複がある
          state.modalState.isOpening.loading = false;
          state.modalState.isOpening.duplicationError = true;
          return;
        } else {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）
          const putResponse = await diContainer.putBaseBusinessUseCase.execute(state.modalState.editState.item);
          if (putResponse.status.type === 'success') {
            state.modalState.isOpening.loading = false;
            state.modalState.isOpening.success = true;
            return;
          }
        }
      case 'error':
      default:
        state.modalState.isOpening.loading = false;
        state.modalState.isOpening.commonError = true;
        return;
    }
  };

  /**
   * 新規追加可能な項目のリストを取得する関数
   * @param isAggregateBusiness  - 編集する取引が集計かどうか
   * @returns - 新規追加可能な項目のリスト
   */
  const getAddableOptions = (isAggregateBusiness: boolean) => {
    let res: string[];
    if (isAggregateBusiness) {
      res = formOptionsAggregateBusiness.filter((option) => {
        return inputTypeKeyMap[option] === 'select';
      });
    } else {
      res = [...formOptionsBusiness];
    }
    return res.map((r) => t(r));
  };

  /**
   * 集計情報を取得する処理
   */
  const fetchTableInfo = async () => {
    state.tableInfo.loadingState.status.type = 'loading';
    await initTableFilter();
    const todayDate: Date = new Date();
    const todayDateWithHyphen = DateService.convertDateStrToDateWithHyphen(todayDate);
    const response = await diContainer.getEditBusinessAggregateBusinessUseCase.execute(
      state.tableInfo.sortState.sortType,
      state.tableInfo.pagingState.pageSize,
      state.tableInfo.pagingState.offset,
      todayDateWithHyphen, // NOTE: 集計情報などを使用しないためとりあえずTodayを突っ込む
      todayDateWithHyphen,
      state.tableInfo.sortState.sortTarget,
      simpleCopy(cachedFilterItems.value)
    );
    switch (response.status.type) {
      case 'success':
        state.tableInfo.tableContentItems = response.status.data.result;
        state.tableInfo.pagingState = response.status.data.pagingOptions;

        // 取得した情報数が0の場合、ゼロマッチステータスにする
        if (state.tableInfo.tableContentItems.length === 0) {
          state.tableInfo.loadingState.status.type = 'zeroMatch';
        } else {
          state.tableInfo.loadingState.status.type = 'success';
        }
        break;
      case 'error':
      default:
        state.tableInfo.loadingState.status.type = 'error';
        return null;
    }
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.tableInfo.pagingState = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    state.renderingKey += 1;
    await _fetchTable();
  };

  /**
   * 編集する取引をセットする処理
   * @param item - 編集するアイテム
   */
  const setEditingBusiness = (item: TableContentItem) => {
    if ('children' in item || 'childrenAsyncStatus' in item) {
      const { children: {} = {}, childrenAsyncStatus: {} = {}, ...aggregateBusinessContent } = item;
      state.modalState.editState.type = 'aggregateBusiness';
      state.modalState.editState.item = aggregateBusinessContent;
    } else {
      const { ...businessContent } = item;
      state.modalState.editState.type = 'business';
      state.modalState.editState.item = businessContent;
    }
  };

  /**
   * 編集モーダルを開く処理
   */
  const openEditModal = () => {
    state.modalState.isOpening.edit = true;
  };

  /**
   * オフセットをリセットする
   */
  const resetOffset = () => {
    // pagingOptionsがnullableなのでこうしてます
    if (state.tableInfo.pagingState) {
      state.tableInfo.pagingState.offset = 0;
    }
  };

  /**
   * 集計行の子取引を取得する処理
   * @param sortType - ソートの向き
   * @param sortTarget - ソートの対象カラム
   */
  const fetchChildBusiness = async (sortType: SortType = 'ASC', sortTarget = 'baseBusinessId') => {
    state.tableInfo.childBusiness.loadingState.status.type = 'loading';
    await initTableFilter();
    const response = await diContainer.getEditBusinessChildBusinessUseCase.execute(
      sortType,
      sortTarget,
      state.tableInfo.childBusiness.pagingState.pageSize,
      state.tableInfo.childBusiness.pagingState.offset,
      []
    );
    switch (response.status.type) {
      case 'success':
        state.tableInfo.childBusiness.tableContentItems = _addAggregateBusinessInfoToChildBusiness(response.status.data.result);
        state.tableInfo.childBusiness.pagingState = response.status.data.pagingOptions;
        // 取得した情報数が0の場合、ゼロマッチステータスにする
        if (state.tableInfo.tableContentItems.length === 0) {
          state.tableInfo.childBusiness.loadingState.status.type = 'zeroMatch';
        } else {
          state.tableInfo.childBusiness.loadingState.status.type = 'success';
        }
        break;
      case 'error':
      default:
        state.tableInfo.childBusiness.loadingState.status.type = 'error';
        return null;
    }
  };

  /**
   * テーブルのソートボタンを押した際の処理
   * @param sortKey - ソートボタンが押された列情報
   * @param isAsc - 昇順かどうか
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
    state.tableInfo.tableContentProperties.contentProperties = state.tableInfo.tableContentProperties.contentProperties.map((property) => {
      if (property.key === sortKey.key) {
        property.sortOption.isAsc = isAsc;
      }
      return property;
    });
    state.tableInfo.sortState.sortType = isAsc ? 'ASC' : 'DESC';
    state.tableInfo.sortState.sortTarget =
      !sortKey.propertyPath?.paths[0] || sortKey.propertyPath?.paths[0] === 'businessId'
        ? state.isBaseBusinessView
          ? 'baseBusinessId'
          : 'aggregateBusinessId'
        : sortKey.propertyPath?.paths[0];
    await _fetchTable();
  };

  /**
   * リードタイムなどの単位をAPIに投げれる形に変換する
   * @param unit - 単位
   */
  const convertDateUnit = (unit: string) => {
    if (!unit) return '';
    switch (unit) {
      case t('日'):
        return 'day';
      case t('週'):
        return 'week';
      case t('月'):
        return 'month';
      default:
        return '';
    }
  };

  /**
   * 関連取引を取得する処理
   */
  const fetchTableInfoBaseBusiness = async () => {
    state.tableInfo.loadingState.status.type = 'loading';
    await initTableFilter();
    const useFilterItems = [...cachedFilterItems.value];
    // TODO: 関連取引を一覧で取ってくるAPIが2025/2/26現在ないので以下の処理でモックしている
    // 一度集計を取得し、そのDを集計の子取引を取得するAPIに渡すことで関連取引を取得
    const response = await diContainer.getEditBusinessChildBusinessUseCase.execute(
      state.tableInfo.sortState.sortType,
      state.tableInfo.sortState.sortTarget,
      state.tableInfo.pagingState.pageSize,
      state.tableInfo.pagingState.offset,
      useFilterItems
    );
    switch (response.status.type) {
      case 'success':
        state.tableInfo.tableContentItems = response.status.data.result;
        state.tableInfo.pagingState = response.status.data.pagingOptions;

        // 取得した情報数が0の場合、ゼロマッチステータスにする
        if (state.tableInfo.tableContentItems.length === 0) {
          state.tableInfo.loadingState.status.type = 'zeroMatch';
        } else {
          state.tableInfo.loadingState.status.type = 'success';
        }
        return;
      case 'error':
      default:
        state.tableInfo.loadingState.status.type = 'error';
        return null;
    }
  };

  /**
   * 一つでもモーダルが開いているかをチェックする関数
   */
  const isModalShown = () => {
    return Object.keys(state.modalState.isOpening).some((modal) => state.modalState.isOpening[modal]);
  };

  /**
   * フィルター適用ボタン押下時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    resetOffset();
    state.renderingKey += 1;
    await _fetchTable();
  };

  // #region private関数

  /**
   * 小取引に集計の情報を付加する処理
   * @param businesses - 子取引の一覧
   */
  const _addAggregateBusinessInfoToChildBusiness = (businesses: IBusiness[]) => {
    return businesses.map(
      (business): IBusiness => ({
        ...business,
        warehouse: state.modalState.editState.item.warehouse,
        supplierName: state.modalState.editState.item.supplierName,
        businessNumber: state.modalState.editState.item.businessNumber,
        businessName: state.modalState.editState.item.businessName,
        package: state.modalState.editState.item.package,
      })
    );
  };

  /**
   * 現在のビューに応じてテーブル情報を取得する
   */
  const _fetchTable = async () => {
    state.isBaseBusinessView ? await fetchTableInfoBaseBusiness() : await fetchTableInfo();
  };

  // #endregion

  return {
    state,
    putEditedAggregateBusiness,
    getAddableOptions,
    fetchTableInfo,
    onChangePaging,
    setEditingBusiness,
    openEditModal,
    resetOffset,
    initialization,
    resetEditModalState,
    fetchChildBusiness,
    sortTableContentItems,
    convertDateUnit,
    fetchTableInfoBaseBusiness,
    putEditedBaseBusiness,
    isModalShown,
    onAppliedFilterModal,
    getFilterItemsValue,
  };
});

/**
 * 取引個別編集画面のState
 */
interface IEditIndividualBusinessState {
  renderingKey: number;
  isBaseBusinessView: boolean;
  tableInfo: {
    tableContentProperties: TableContentHeader;
    tableContentItems: TableContentItem[];
    selectOptionInfo: {
      [key: string]: string[];
    };
    isReset: boolean;
    loadingState: LoadingState;
    pagingState: IPagingOptions;
    sortState: {
      sortType: SortType;
      sortTarget: string;
    };
    filterState: NarrowState[];
    childBusiness: {
      tableContentItems: TableContentItem[];
      pagingState: IPagingOptions;
      loadingState: LoadingState;
      returnCount: number;
    };
  };
  modalState: {
    isOpening: {
      edit: boolean;
      childBusinessList: boolean;
      loading: boolean;
      success: boolean;
      commonError: boolean;
      duplicationError: boolean;
    };
    editState: {
      type: 'aggregateBusiness' | 'business' | 'none';
      item: IBusiness;
      isEdited: boolean;
    };
  };
}
